import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Stack,
  Autocomplete,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EuroIcon from "@mui/icons-material/Euro";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import { NumericFormat } from "react-number-format";
import { italianProvinces } from "./data/zoneMap";
import { DEFAULT_GAP_LIMITS } from "./data/satellitare";

const DURATIONS = [
  { value: "12", label: "12 mesi" },
  { value: "24", label: "24 mesi" },
  { value: "36", label: "36 mesi" },
  { value: "48", label: "48 mesi" },
  { value: "60", label: "60 mesi" },
];

const formatLimit = (limit) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
  }).format(limit);

export default function QuoteForm({
  formData,
  setFormData,
  onCalculate,
  onClose,
  isLoading,
  errors,
  submitLabel = "Calcola",
}) {
  // il massimale si sceglie prima di calcolare: la lista è quella di listino
  const gapLimits = DEFAULT_GAP_LIMITS;

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const selectedProvinceOption =
    italianProvinces.find((p) => p.code === formData.provinceCode) ||
    (formData.province
      ? italianProvinces.find(
          (p) => p.name.toLowerCase() === formData.province.toLowerCase(),
        ) || null
      : null);

  if (isLoading) {
    return (
      <Card elevation={3}>
        <CardContent sx={{ py: 8 }}>
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <CircularProgress size={72} thickness={3} />
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "@keyframes quotePulse": {
                    "0%, 100%": { opacity: 0.35, transform: "scale(0.9)" },
                    "50%": { opacity: 1, transform: "scale(1.1)" },
                  },
                  animation: "quotePulse 1.4s ease-in-out infinite",
                }}
              >
                <SearchIcon color="primary" />
              </Box>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Calcolo del preventivo in corso
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, maxWidth: 420, mx: "auto" }}
              >
                Stiamo contattando i nostri partner per trovare le migliori
                soluzioni disponibili per te.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={3}>
      <CardContent>
        <Stack spacing={3}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Compila i dati richiesti per ottenere i pacchetti disponibili.
            </Typography>
          </Box>
          <Box>
            <Grid container spacing={2} wrap="wrap">
              <Grid item xs={12} sm={4}>
                <TextField //non obbligatorio
                  fullWidth
                  label="Targa"
                  placeholder="AA111AA"
                  value={formData.targa || ""}
                  onChange={(e) => handleChange("targa", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField //non obbligatorio
                  fullWidth
                  label="Marca"
                  placeholder="Marca"
                  value={formData.marca || ""}
                  onChange={(e) => handleChange("marca", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField //non obbligatorio
                  fullWidth
                  label="Modello"
                  placeholder="Modello"
                  value={formData.modello || ""}
                  onChange={(e) => handleChange("modello", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField //obbligatorio
                  autoFocus
                  fullWidth
                  required
                  size="small"
                  type="text"
                  label="Cliente"
                  value={formData.cliente || ""}
                  onChange={(e) => handleChange("cliente", e.target.value)}
                  error={!!errors?.cliente}
                  helperText={errors?.cliente}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  fullWidth
                  options={italianProvinces}
                  value={selectedProvinceOption}
                  size="small"
                  disablePortal
                  ListboxProps={{
                    sx: {
                      maxHeight: 120,
                      overflowY: "auto",
                    },
                  }}
                  noOptionsText="Nessuna provincia trovata"
                  isOptionEqualToValue={(option, value) =>
                    option.code === value?.code
                  }
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : `${option.code} - ${option.name}`
                  }
                  onChange={(_, newValue) => {
                    handleChange("provinceCode", newValue?.code || "");
                    handleChange("province", newValue?.name || "");
                  }}
                  onInputChange={(_, newInputValue, reason) => {
                    // solo la digitazione dell'utente aggiorna il campo:
                    // il "reset" post-selezione riporterebbe la label "AG - Agrigento"
                    if (reason !== "input") return;
                    if (!newInputValue) {
                      handleChange("provinceCode", "");
                      handleChange("province", "");
                      return;
                    }
                    handleChange("province", newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <LocationOnIcon fontSize="small" /> Provincia *
                        </Box>
                      }
                      fullWidth
                      error={!!errors?.province}
                      helperText={errors?.province}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.code}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          gap: 1,
                        }}
                      >
                        <span>{option.name}</span>
                        <Typography variant="body2" color="text.secondary">
                          {option.code}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <NumericFormat
                  customInput={TextField}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  allowNegative={false}
                  valueIsNumericString
                  fullWidth
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <EuroIcon fontSize="small" /> Valore veicolo *
                    </Box>
                  }
                  placeholder="25.000"
                  value={formData.vehicleValue || ""}
                  // in formData resta il valore numerico non formattato ("25000.5")
                  onValueChange={({ value }) =>
                    handleChange("vehicleValue", value)
                  }
                  error={!!errors?.vehicleValue}
                  helperText={errors?.vehicleValue}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ mr: 1, color: "text.secondary" }}>
                        €
                      </Typography>
                    ),
                  }}
                  sx={{ flex: "1 1 180px", minWidth: 180 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <CalendarTodayIcon fontSize="small" /> Durata *
                    </Box>
                  }
                  value={formData.duration || ""}
                  onChange={(e) => handleChange("duration", e.target.value)}
                  error={!!errors?.duration}
                  helperText={errors?.duration}
                  sx={{ flex: "1 1 180px", minWidth: 180 }}
                >
                  {DURATIONS.map((d) => (
                    <MenuItem key={d.value} value={d.value}>
                      {d.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <SatelliteAltIcon fontSize="small" /> Massimale GAP *
                    </Box>
                  }
                  value={formData.gapLimit || ""}
                  onChange={(e) => handleChange("gapLimit", e.target.value)}
                  error={!!errors?.gapLimit}
                  helperText={
                    errors?.gapLimit || "GAP Furto + RSA con satellitare"
                  }
                  sx={{ flex: "1 1 180px", minWidth: 180 }}
                >
                  {gapLimits.map((limit) => (
                    <MenuItem key={limit} value={String(limit)}>
                      {formatLimit(limit)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {onClose && (
              <Button
                variant="outlined"
                size="large"
                onClick={onClose}
                sx={{ px: 3, py: 1.2, fontWeight: 700 }}
              >
                Chiudi
              </Button>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={onCalculate}
              disabled={isLoading}
              startIcon={
                isLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SearchIcon />
                )
              }
              sx={{
                px: 3,
                py: 1.2,
                fontWeight: 700,
                minWidth: 140,
                alignSelf: "stretch",
              }}
            >
              {isLoading ? "Calcolo in corso..." : submitLabel}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
